import { useState, type FormEvent } from 'react';
import { call } from '@edil/api-client';
import { Alert, Button, Card, CardHeader, TextField } from '@edil/ui';
import { useApp } from '@/lib/app';
import { errorText } from '@/lib/format';

/** Two steps (BUILD_PLAN §5.13): password, then the authenticator code. TOTP is set up on first login. */
export function LoginScreen() {
  const { api, signIn } = useApp();
  const [step, setStep] = useState<'password' | 'code'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfa, setMfa] = useState<{ token: string; secret: string | null; url: string | null } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submitPassword(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await call(api.POST('/admin/v1/auth/login', { body: { email, password } }));
      setMfa({ token: res.mfa_token, secret: res.totp_enrolment?.secret ?? null, url: res.totp_enrolment?.otpauth_url ?? null });
      setStep('code');
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    if (!mfa) return;
    setBusy(true);
    setError(null);
    try {
      const res = await call(api.POST('/admin/v1/auth/mfa', { body: { mfa_token: mfa.token, code: code.trim() } }));
      signIn(res.token, res.admin);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <Card padding="lg" className="authCard">
        <CardHeader title="Edil back office" subtitle={step === 'password' ? 'Sign in with your admin account.' : mfa?.secret ? 'Set up your authenticator app.' : 'Enter the code from your authenticator app.'} />
        {error && <Alert tone="danger">{error}</Alert>}
        {step === 'password' ? (
          <form className="form" onSubmit={(e) => void submitPassword(e)}>
            <TextField label="Email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} />
            <TextField label="Password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />
            <Button type="submit" block loading={busy} disabled={!email || !password}>
              Continue
            </Button>
          </form>
        ) : (
          <form className="form" onSubmit={(e) => void submitCode(e)}>
            {mfa?.secret && (
              <>
                <p className="small muted">Add this key to Google Authenticator, Authy or 1Password (time-based, 6 digits), then enter the code it shows.</p>
                <div className="secret" data-testid="totp-secret">
                  {mfa.secret.replace(/(.{4})/g, '$1 ').trim()}
                </div>
                {mfa.url && (
                  <a className="small" href={mfa.url}>
                    Open in an authenticator app on this device
                  </a>
                )}
              </>
            )}
            <TextField label="6-digit code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} autoFocus />
            <Button type="submit" block loading={busy} disabled={!/^\d{6}$/.test(code.trim())}>
              Sign in
            </Button>
            <Button variant="ghost" block onClick={() => setStep('password')}>
              Back
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}
